import { FormControl, FormHelperText, MenuItem, TextField } from '@material-ui/core';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { LabelWithTooltip, useScaffolderTooltip } from '../LabelWithTooltip';

// -----------------------------------------------------------------------------------
// Campo de texto/desplegable con ayuda contextual, configurable desde el template.yaml.
//
// Evita tener que escribir un custom field por cada campo que solo necesita un tooltip.
// El tipo de control se deduce del propio JSON Schema:
//
//   - `enum` presente            -> desplegable (usa `enumNames` como etiquetas)
//   - `ui:options.multiline`     -> textarea
//   - resto                      -> input de texto
//
// Uso desde el template:
//
//   metal:
//     type: string
//     title: Metal
//     enum: [BRONCE, PLATA, ORO, PLATINO]
//     default: BRONCE
//     ui:field: TooltipField
//     ui:disabled: true
//     ui:options:
//       tooltipKey: iacMvp.metal
//
// `tooltipKey` apunta al catálogo de textos traducidos (translation/scaffolder-tooltips).
// Para un texto literal sin traducir existe `tooltip`, pensado solo para prototipos.
// Si no se indica ninguno, el campo se pinta sin icono: así un mismo formulario puede
// mezclar campos con y sin ayuda.
// -----------------------------------------------------------------------------------

interface TooltipFieldUiOptions {
  /** Clave del catálogo de tooltips traducidos, p. ej. "iacMvp.metal". */
  tooltipKey?: string;
  /** Texto literal (sin traducir). `tooltipKey` tiene prioridad sobre este. */
  tooltip?: string;
  /** Renderiza un textarea en lugar de un input de una línea. */
  multiline?: boolean;
  /** Filas visibles del textarea. Solo aplica con `multiline`. */
  rows?: number;
  /** Texto de ayuda bajo el campo. Por defecto usa `schema.description`. */
  helperText?: string;
  /** Texto del elemento vacío del desplegable. Sin él, el desplegable no admite vaciarse. */
  placeholder?: string;
}

export const TooltipField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
  disabled,
}: FieldExtensionComponentProps<string>) => {
  const options = (uiSchema?.['ui:options'] ?? {}) as TooltipFieldUiOptions;

  // `ui:disabled` no llega en la prop `disabled` cuando el campo es un `ui:field`
  // propio, así que se lee también del uiSchema.
  const isDisabled = disabled || uiSchema?.['ui:disabled'] === true;

  const tooltipFromKey = useScaffolderTooltip(options.tooltipKey);
  const tooltip = tooltipFromKey ?? options.tooltip;

  // `enumNames` no forma parte de JSON Schema, pero RJSF lo admite para etiquetar
  // opciones; de ahí que no esté en el tipo de `schema`.
  const enumValues = (schema.enum ?? []) as string[];
  const enumNames = ((schema as { enumNames?: string[] }).enumNames ?? []) as string[];
  const isSelect = enumValues.length > 0;

  const error = rawErrors?.[0];
  const helperText = error ?? options.helperText ?? schema.description;

  return (
    <FormControl margin="normal" required={required} fullWidth error={!!error}>
      <LabelWithTooltip
        label={schema.title ?? ''}
        tooltip={tooltip}
        required={required}
      />

      <TextField
        select={isSelect}
        value={formData ?? ''}
        onChange={e => onChange(e.target.value)}
        disabled={isDisabled}
        error={!!error}
        multiline={!isSelect && options.multiline}
        rows={options.multiline ? options.rows ?? 4 : undefined}
        inputProps={{ maxLength: schema.maxLength }}
        placeholder={!isSelect ? options.placeholder : undefined}
        fullWidth
      >
        {isSelect &&
          enumValues.map((value, index) => (
            <MenuItem key={value} value={value}>
              {enumNames[index] ?? value}
            </MenuItem>
          ))}
      </TextField>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

// -----------------------------------------------------------------------------------
// Registro del Custom Field
//
// Sin `validation`: las reglas (required, maxLength, enum) ya salen del JSON Schema y
// las aplica el propio scaffolder.
// -----------------------------------------------------------------------------------
export const TooltipFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TooltipField',
    component: TooltipField,
  }),
);
